import { useEffect, useState } from "react";
import axios from "axios";
import { useLang } from "../i18n/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PrivateRequestsPanel({ refreshKey = 0 }) {
  const { t } = useLang();
  const password = localStorage.getItem("st_admin_pw") || "";
  const headers = { "X-Admin-Password": password };
  const [reqs, setReqs] = useState([]);
  const [busy, setBusy] = useState(null);

  const load = async () => {
    const r = await axios.get(`${API}/admin/private-requests`, { headers });
    setReqs(r.data);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [refreshKey]);

  const decide = async (req, status) => {
    setBusy(req.id);
    try {
      const note = status === "declined"
        ? (window.prompt("Razón / Reason (opcional):") || "")
        : "";
      const r = await axios.post(
        `${API}/admin/private-requests/${req.id}/decision`,
        { status, admin_note: note },
        { headers }
      );
      setReqs((arr) => arr.map((x) => (x.id === req.id ? r.data : x)));
    } finally {
      setBusy(null);
    }
  };

  const pending = reqs.filter((r) => r.status === "pending");
  const others = reqs.filter((r) => r.status !== "pending").slice(0, 10);

  if (reqs.length === 0) return null;

  const Badge = ({ s }) => {
    if (s === "approved") return <span className="px-2 py-0.5 rounded-full bg-emerald-500/15 border border-emerald-500/40 text-emerald-400 text-[10px] uppercase tracking-wider">{t("approved")}</span>;
    if (s === "declined") return <span className="px-2 py-0.5 rounded-full bg-[#e05555]/15 border border-[#e05555]/40 text-[#e05555] text-[10px] uppercase tracking-wider">{t("declined")}</span>;
    return <span className="px-2 py-0.5 rounded-full bg-amber-500/15 border border-amber-500/40 text-amber-400 text-[10px] uppercase tracking-wider">{t("pending")}</span>;
  };

  const Row = ({ r }) => (
    <div
      key={r.id}
      data-testid={`req-${r.id}`}
      className="rounded-lg bg-[#0f0f0f] border border-[#252525] p-3.5 flex flex-col sm:flex-row sm:items-center gap-3"
    >
      <div className="flex-shrink-0 flex items-center gap-3">
        <span className="font-bebas text-xl text-[#e8559a] tracking-widest w-10">
          #{r.player_number}
        </span>
        <Badge s={r.status} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm text-white font-medium truncate">{r.player_name}</div>
        <div className="text-xs text-neutral-400 mt-0.5">
          📅 {r.date} {r.time} · 📞 {r.phone}
        </div>
        {r.notes && <div className="text-xs text-neutral-500 mt-1 truncate">"{r.notes}"</div>}
      </div>
      {r.status === "pending" && (
        <div className="flex gap-2 flex-shrink-0">
          <button
            onClick={() => decide(r, "approved")}
            disabled={busy === r.id}
            data-testid={`approve-${r.id}`}
            className="px-3 py-1.5 rounded-md bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-xs font-medium hover:bg-emerald-500/20 transition-colors disabled:opacity-50"
          >
            ✓ {t("approve")}
          </button>
          <button
            onClick={() => decide(r, "declined")}
            disabled={busy === r.id}
            data-testid={`decline-${r.id}`}
            className="px-3 py-1.5 rounded-md bg-[#e05555]/10 border border-[#e05555]/30 text-[#e05555] text-xs font-medium hover:bg-[#e05555]/20 transition-colors disabled:opacity-50"
          >
            ✕ {t("decline")}
          </button>
        </div>
      )}
    </div>
  );

  return (
    <div className="rounded-2xl bg-[#161616] border border-amber-500/30 p-5 sm:p-6 mb-6" data-testid="private-requests-panel">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-bebas text-xl tracking-widest text-white flex items-center gap-2">
          ⚡ {t("private_requests")}
          {pending.length > 0 && (
            <span className="px-2 py-0.5 rounded-full bg-amber-500 text-white text-xs font-bold">
              {pending.length}
            </span>
          )}
        </h3>
      </div>
      <div className="space-y-2">
        {pending.length > 0 ? pending.map((r) => <Row key={r.id} r={r} />) : (
          <div className="text-center py-3 text-neutral-500 text-sm">{t("no_requests")}</div>
        )}
      </div>
      {others.length > 0 && (
        <details className="mt-4">
          <summary className="cursor-pointer text-xs text-neutral-500 hover:text-[#e8559a] uppercase tracking-widest">
            Historial · History ({others.length})
          </summary>
          <div className="mt-2 space-y-2 opacity-70">
            {others.map((r) => <Row key={r.id} r={r} />)}
          </div>
        </details>
      )}
    </div>
  );
}
